import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNumberConversion } from "./number-conversion";
import { useAppSelector, selectors } from "store";
import { round, Unit } from "utility/number";
export var SystemErrorKey;
(function (SystemErrorKey) {
    SystemErrorKey["CYCLE_TIME_NOT_REACHABLE"] = "system_errors.cycle_time_not_reachable";
    SystemErrorKey["PART_SECURED_NOT_REACHABLE"] = "system_errors.part_secured_level_not_reachable";
    SystemErrorKey["ENERGY_SAVING_NOT_REACHABLE"] = "system_errors.energy_saving_level_not_reachable";
    SystemErrorKey["FEED_PRESSURE_TOO_LOW"] = "system_errors.feed_pressure_too_low";
    SystemErrorKey["FEED_PRESSURE_TOO_HIGH"] = "system_errors.feed_pressure_too_high";
    SystemErrorKey["TUBING_MUCH_TOO_SMALL"] = "system_errors.tubing_much_too_small";
})(SystemErrorKey || (SystemErrorKey = {}));
// Provides the details that we send into a few of the messages
var feedPressureDetails = function (sanityCheckData, convertNumber) {
    var roundedFeedPressureActual = round(sanityCheckData.feedPressureActual, 2);
    var roundedFeedPressureRangeMin = round(sanityCheckData.feedPressureRangeMin, 2);
    var roundedFeedPressureRangeMax = round(sanityCheckData.feedPressureRangeMax, 2);
    return {
        feedPressureActual: convertNumber(roundedFeedPressureActual, Unit.MPA)
            .formatted,
        feedPressureRangeMin: convertNumber(roundedFeedPressureRangeMin, Unit.MPA)
            .formatted,
        feedPressureRangeMax: convertNumber(roundedFeedPressureRangeMax, Unit.MPA)
            .formatted,
    };
};
/*
 * Translates a "part secured not reached" message. When the tool lifts several
 * boxes the system fails PS as soon as one box's cups do, so the message names
 * those boxes (the key's `_for_boxes` variant) and the user knows which box
 * needs more cups or capacity. With one lifted box the plain key is used.
 */
export var usePartSecuredNotReachedMessage = function () {
    var boxNumbers = useAppSelector(selectors.selectBoxNumbersNotReachingPartSecured);
    var t = useTranslation().t;
    return useCallback(function (key) {
        return boxNumbers.length
            ? t("".concat(key, "_for_boxes"), {
                count: boxNumbers.length,
                boxNumbers: boxNumbers.map(function (n) { return "#".concat(n); }).join(", "),
            })
            : t(key);
    }, [boxNumbers, t]);
};
export var useSystemErrorMessages = function () {
    var sanityCheckData = useAppSelector(selectors.selectSanityCheckData);
    var partSecuredNotReachedMessage = usePartSecuredNotReachedMessage();
    var hasSettledUserCalculation = useAppSelector(selectors.selectHasSettledUserCalculation);
    var t = useTranslation().t;
    var convertNumber = useNumberConversion();
    var messages = useMemo(function () {
        var errors = [];
        if (sanityCheckData) {
            if (!sanityCheckData.cycleTimeReachable) {
                errors.push(t(SystemErrorKey.CYCLE_TIME_NOT_REACHABLE));
            }
            // Part secured uses the cup-aware check, which can flag systems the
            // pump-only sanity check passes. Only surface it once the user has run
            // a calculation, so it doesn't appear during background pre-calculation.
            if (hasSettledUserCalculation && !sanityCheckData.partSecuredReachable) {
                errors.push(partSecuredNotReachedMessage(SystemErrorKey.PART_SECURED_NOT_REACHABLE));
            }
            if (sanityCheckData.energySaveAvailable &&
                !sanityCheckData.energySaveReachable) {
                errors.push(t(SystemErrorKey.ENERGY_SAVING_NOT_REACHABLE));
            }
            if (sanityCheckData.feedPressureMessage === "feedpressure_too_low") {
                errors.push(t(SystemErrorKey.FEED_PRESSURE_TOO_LOW, feedPressureDetails(sanityCheckData, convertNumber)));
            }
            if (sanityCheckData.feedPressureMessage === "feedpressure_too_high") {
                errors.push(t(SystemErrorKey.FEED_PRESSURE_TOO_HIGH, feedPressureDetails(sanityCheckData, convertNumber)));
            }
            if (sanityCheckData.freeRunningVacuumLevelMessage ===
                "tubing_much_too_small") {
                errors.push(t(SystemErrorKey.TUBING_MUCH_TOO_SMALL));
            }
        }
        return errors;
    }, [
        convertNumber,
        sanityCheckData,
        hasSettledUserCalculation,
        partSecuredNotReachedMessage,
        t,
    ]);
    return { messages: messages };
};
export var SystemWarningKey;
(function (SystemWarningKey) {
    SystemWarningKey["CALCULATIONS_CHANGED"] = "system_warnings.calculations_changed";
    SystemWarningKey["LIFTING_FORCE_NOT_ACHIEVED"] = "system_warnings.required_lifting_force_not_achieved";
    SystemWarningKey["LIFTING_FORCE_NOT_ACHIEVED_FOR_BOXES"] = "system_warnings.required_lifting_force_not_achieved_for_boxes";
})(SystemWarningKey || (SystemWarningKey = {}));
// TODO: All warnings should have a pre and post calculation message in the future.
var PrePostSystemWarningKeys = {
    preCalculation: {
        tubingTooLarge: "system_info.tubing_too_large.precalculate",
        tubingTooSmall: "system_info.tubing_too_small.precalculate",
        feedPressureBelowNormal: "system_warnings.feedpressure_below_normal_operating_range.precalculate",
        feedPressureAboveNormal: "system_warnings.feedpressure_above_normal_operating_range.precalculate",
        feedPressureDropTubingTooSmall: "system_warnings.feed_pressure_drop_tubing_too_small.precalculate",
        feedPressureDropTubingTooLarge: "system_warnings.feed_pressure_drop_tubing_too_large.precalculate",
    },
    postCalculation: {
        tubingTooLarge: "system_info.tubing_too_large.postcalculate",
        tubingTooSmall: "system_info.tubing_too_small.postcalculate",
        feedPressureBelowNormal: "system_warnings.feedpressure_below_normal_operating_range.postcalculate",
        feedPressureAboveNormal: "system_warnings.feedpressure_above_normal_operating_range.postcalculate",
        feedPressureDropTubingTooSmall: "system_warnings.feed_pressure_drop_tubing_too_small.postcalculate",
        feedPressureDropTubingTooLarge: "system_warnings.feed_pressure_drop_tubing_too_large.postcalculate",
    },
};
export var useSystemPerformanceValidityMessage = function () {
    var performanceData = useAppSelector(selectors.selectPerformanceData);
    var isValid = useAppSelector(selectors.selectIsPerformanceDataValid);
    var t = useTranslation().t;
    return performanceData && !isValid
        ? t(SystemWarningKey.CALCULATIONS_CHANGED)
        : null;
};
export var useSystemWarningMessages = function () {
    var performanceValidityMessage = useSystemPerformanceValidityMessage();
    var sanityCheckData = useAppSelector(selectors.selectSanityCheckData);
    var outputDefineParameters = useAppSelector(selectors.selectOutputDefineParameters);
    var performanceData = useAppSelector(selectors.selectActivePerformanceData);
    var liftedBoxes = useAppSelector(selectors.selectLiftedBoxLiftingForces);
    var isLoadingSystemData = useAppSelector(selectors.selectIsSystemDataLoading);
    var isPerformanceLoading = useAppSelector(selectors.selectIsPerformanceLoading);
    var isPerformanceDataValid = useAppSelector(selectors.selectIsPerformanceDataValid) &&
        !isLoadingSystemData &&
        !isPerformanceLoading;
    var isPostCalculation = useAppSelector(selectors.selectIsPostCalculation);
    var t = useTranslation().t;
    var convertNumber = useNumberConversion();
    var messages = useMemo(function () {
        var warnings = [];
        if (performanceValidityMessage) {
            warnings.push(performanceValidityMessage);
        }
        if (sanityCheckData) {
            var keys = isPostCalculation
                ? PrePostSystemWarningKeys.postCalculation
                : PrePostSystemWarningKeys.preCalculation;
            if (sanityCheckData.freeRunningVacuumLevelMessage === "tubing_too_large") {
                warnings.push(t(keys.tubingTooLarge));
            }
            if (sanityCheckData.freeRunningVacuumLevelMessage === "tubing_too_small") {
                warnings.push(t(keys.tubingTooSmall));
            }
            if (sanityCheckData.feedPressureMessage ===
                "feedpressure_below_normal_operating_range") {
                warnings.push(t(keys.feedPressureBelowNormal, feedPressureDetails(sanityCheckData, convertNumber)));
            }
            if (sanityCheckData.feedPressureMessage ===
                "feedpressure_above_normal_operating_range") {
                warnings.push(t(keys.feedPressureAboveNormal, feedPressureDetails(sanityCheckData, convertNumber)));
            }
            if (sanityCheckData.feedPressureDropQuality === "tubing_too_small") {
                warnings.push(t(keys.feedPressureDropTubingTooSmall, {
                    feedPressureDrop: convertNumber(round(sanityCheckData.feedPressureDrop, 2), Unit.MPA).formatted,
                }));
            }
            if (sanityCheckData.feedPressureDropQuality === "tubing_too_large") {
                warnings.push(t(keys.feedPressureDropTubingTooLarge, {
                    feedPressureDrop: convertNumber(round(sanityCheckData.feedPressureDrop, 2), Unit.MPA).formatted,
                }));
            }
        }
        if (outputDefineParameters) {
            // Mirror the Lifting forces box (which boxes count and their verdicts
            // come from the same selector): with several lifted boxes the aggregate
            // comparison is meaningless — "required" is per box — so the warning is
            // raised from the per-box verdicts and names the boxes that fall short.
            // Per-box forces only exist once a full calculation is in, and the
            // verdicts are only meaningful against a positive requirement — the
            // same gate as the box's weakest-box headline.
            var multiBoxVerdicts = isPerformanceDataValid &&
                liftedBoxes.length > 1 &&
                liftedBoxes[0].box.requiredVertical > 0 &&
                liftedBoxes[0].box.requiredHorizontal > 0
                ? liftedBoxes
                : undefined;
            if (multiBoxVerdicts) {
                var failingBoxNumbers = multiBoxVerdicts
                    .filter(function (_a) {
                    var box = _a.box;
                    return !box.partSecured;
                })
                    .map(function (_a) {
                    var boxNumber = _a.boxNumber;
                    return "#".concat(boxNumber);
                });
                if (failingBoxNumbers.length) {
                    warnings.push(t(SystemWarningKey.LIFTING_FORCE_NOT_ACHIEVED_FOR_BOXES, {
                        count: failingBoxNumbers.length,
                        boxNumbers: failingBoxNumbers.join(", "),
                    }));
                }
            }
            else {
                // Single box (or preliminary values): when a full calculation is
                // available the "actual" forces are the values at Part Secured;
                // otherwise we fall back to the preliminary define-parameters output.
                // Comparing with the same rounding/conversion as the box keeps the
                // warning in sync with the red/green status shown there.
                var actualHorizontal = isPerformanceDataValid
                    ? performanceData === null || performanceData === void 0 ? void 0 : performanceData.totalHorizontalLiftForceAtPS
                    : outputDefineParameters.totalLiftingForceHorizontal;
                var actualVertical = isPerformanceDataValid
                    ? performanceData === null || performanceData === void 0 ? void 0 : performanceData.totalVerticalLiftForceAtPS
                    : outputDefineParameters.totalLiftingForceVertical;
                var convertForce = function (value) {
                    return convertNumber(value ? Math.round(value) : 0, Unit.N).value;
                };
                var horizontalForceOk = convertForce(actualHorizontal) >=
                    convertForce(outputDefineParameters.requiredLiftingForceHorizontal);
                var verticalForceOk = convertForce(actualVertical) >=
                    convertForce(outputDefineParameters.requiredLiftingForceVertical);
                if (!horizontalForceOk || !verticalForceOk) {
                    warnings.push(t(SystemWarningKey.LIFTING_FORCE_NOT_ACHIEVED));
                }
            }
        }
        return warnings;
    }, [
        performanceValidityMessage,
        sanityCheckData,
        outputDefineParameters,
        performanceData,
        liftedBoxes,
        isPerformanceDataValid,
        isPostCalculation,
        t,
        convertNumber,
    ]);
    return { messages: messages, isLoading: isLoadingSystemData };
};
export var SystemInfoKey;
(function (SystemInfoKey) {
    SystemInfoKey["FEED_PRESSURE_DROP_TUBING_CORRECTLY_SIZED"] = "system_info.feed_pressure_drop_tubing_correctly_sized";
})(SystemInfoKey || (SystemInfoKey = {}));
var SystemInfoKeys = {
    preCalculation: {
        tubingCorrectlySized: "system_info.tubing_correctly_sized.precalculate",
        feedPressureWithingNormalOperatingRange: "system_info.feed_pressure_within_normal_operating_range.precalculate",
    },
    postCalculation: {
        tubingCorrectlySized: "system_info.tubing_correctly_sized.postcalculate",
        feedPressureWithingNormalOperatingRange: "system_info.feed_pressure_within_normal_operating_range.postcalculate",
    },
};
export var useSystemInfoMessages = function () {
    var sanityCheckData = useAppSelector(selectors.selectSanityCheckData);
    var isPostCalculation = useAppSelector(selectors.selectIsPostCalculation);
    var t = useTranslation().t;
    var convertNumber = useNumberConversion();
    var messages = useMemo(function () {
        var messages = [];
        if (sanityCheckData) {
            var infoKeys = isPostCalculation
                ? SystemInfoKeys.postCalculation
                : SystemInfoKeys.preCalculation;
            if (sanityCheckData.freeRunningVacuumLevelMessage ===
                "tubing_correctly_sized") {
                messages.push(t(infoKeys.tubingCorrectlySized));
            }
            if (sanityCheckData.feedPressureMessage ===
                "feedpressure_within_normal_operating_range") {
                messages.push(t(infoKeys.feedPressureWithingNormalOperatingRange, feedPressureDetails(sanityCheckData, convertNumber)));
            }
            if (sanityCheckData.feedPressureDropQuality === "tubing_correctly_sized") {
                messages.push(t(SystemInfoKey.FEED_PRESSURE_DROP_TUBING_CORRECTLY_SIZED, {
                    feedPressureDrop: convertNumber(round(sanityCheckData.feedPressureDrop, 2), Unit.MPA).formatted,
                }));
            }
        }
        return messages;
    }, [convertNumber, isPostCalculation, sanityCheckData, t]);
    return { messages: messages };
};
export var EnergySavingDisabledKey;
(function (EnergySavingDisabledKey) {
    EnergySavingDisabledKey["PUMP_WITHOUT_ES"] = "dashboard.es_disabled.pump_without_es";
    EnergySavingDisabledKey["PARAMETER_OFF"] = "dashboard.es_disabled.parameter_off";
    EnergySavingDisabledKey["NO_CALCULATION"] = "dashboard.es_disabled.no_calculation";
    EnergySavingDisabledKey["NOT_REACHED"] = "dashboard.es_disabled.not_reached";
})(EnergySavingDisabledKey || (EnergySavingDisabledKey = {}));
/**
 * Explains why the dashboard's "Energy saving" toggle can't be switched on,
 * or null when it can. The toggle needs a calculation that reached the energy
 * saving level; this works backwards from the reasons that prevents it, most
 * actionable first.
 *
 * The energy-saving parameter is checked before the pump selection: while the
 * parameter is off, Tacton only offers pumps without ES, so telling the user to
 * pick an ES pump would send them to a picker where none exists. Turning the
 * parameter on is the step that unlocks those pumps.
 *
 * `hasReachedEnergySave` describes the calculation currently on screen, which
 * is only replaced when the user runs a simulation (see the preload pattern in
 * the systemPerformance slice). The parameter and pump checks read the live
 * system instead, so after e.g. swapping to an ES pump they clear immediately
 * while the shown result is still the old one. In that window the result is
 * stale (`isPerformanceDataValid` is false), and the only honest advice is to
 * run a simulation — not that "the ES level was not reached", which describes
 * the previous system.
 */
export var useEnergySavingDisabledReason = function () {
    var hasReachedEnergySave = useAppSelector(selectors.selectHasReachedEnergySave);
    var pumpsWithoutEnergySaving = useAppSelector(selectors.selectPumpsWithoutEnergySaving);
    var isEnergySavingParameterOn = useAppSelector(selectors.selectIsEnergySavingParameterOn);
    var sanityCheckData = useAppSelector(selectors.selectSanityCheckData);
    var performanceData = useAppSelector(selectors.selectPerformanceData);
    var isPerformanceDataValid = useAppSelector(selectors.selectIsPerformanceDataValid);
    var isPerformanceDataPending = useAppSelector(selectors.selectIsPerformanceDataPending);
    var t = useTranslation().t;
    return useMemo(function () {
        if (hasReachedEnergySave)
            return null;
        if (!isEnergySavingParameterOn) {
            return t(EnergySavingDisabledKey.PARAMETER_OFF);
        }
        if (pumpsWithoutEnergySaving.length > 0) {
            return t(EnergySavingDisabledKey.PUMP_WITHOUT_ES, {
                pumps: pumpsWithoutEnergySaving.map(function (pump) { return pump.label; }).join(", "),
            });
        }
        // The sanity check is refreshed by the background recalculation that a
        // system edit queues, so it is current once that has landed. While it is
        // still pending it describes the previous system and must not be trusted.
        if (!isPerformanceDataPending &&
            (sanityCheckData === null || sanityCheckData === void 0 ? void 0 : sanityCheckData.energySaveAvailable) &&
            !sanityCheckData.energySaveReachable) {
            return t(SystemErrorKey.ENERGY_SAVING_NOT_REACHABLE);
        }
        if (!performanceData || !isPerformanceDataValid) {
            return t(EnergySavingDisabledKey.NO_CALCULATION);
        }
        return t(EnergySavingDisabledKey.NOT_REACHED);
    }, [
        hasReachedEnergySave,
        pumpsWithoutEnergySaving,
        isEnergySavingParameterOn,
        sanityCheckData,
        performanceData,
        isPerformanceDataValid,
        isPerformanceDataPending,
        t,
    ]);
};
